import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  AutoAwesome as AIIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../utils/api';
import { skipWhenOffline, useIsOffline } from '../../offline';

export interface UploadedAttachment {
  id: string;
  fileName: string;
  mimeType?: string;
  fileSize?: number;
  storageUrl?: string;
  aiExtracted?: any;
  createdAt: string;
}

interface FileDropZoneProps {
  horseId?: string;
  parentType?: string;
  parentId?: string;
  fieldKey?: string;
  category?: string;
  accept?: string;
  multiple?: boolean;
  value?: UploadedAttachment[];
  onChange?: (attachments: UploadedAttachment[]) => void;
  onAIExtract?: (attachmentId: string, extracted: any) => void;
  templateSchema?: any[];
  readOnly?: boolean;
  /** Resolve a parent id just-in-time when one isn't set yet (e.g. save a
   *  brand-new SOAP note as a draft on first upload, so the user doesn't have
   *  to save first). Returns the new parentId, or undefined to abort. */
  ensureParentId?: () => Promise<string | undefined>;
}

const getFileIcon = (mimeType?: string) => {
  if (!mimeType) return <FileIcon />;
  if (mimeType.startsWith('image/')) return <ImageIcon />;
  if (mimeType === 'application/pdf') return <PdfIcon />;
  return <FileIcon />;
};

const formatBytes = (bytes?: number) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileDropZone: React.FC<FileDropZoneProps> = ({
  horseId,
  parentType,
  parentId,
  fieldKey,
  category,
  accept = '*',
  multiple = false,
  value = [],
  onChange,
  onAIExtract,
  templateSchema,
  readOnly = false,
  ensureParentId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef<HTMLInputElement>(null);
  // Attachments are online-only: blobs aren't replicated (manifest only) and
  // file bytes are never queued offline. Reactive so the zone re-enables live.
  const isOffline = useIsOffline();
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});

  // Load image previews via authenticated API
  useEffect(() => {
    const imageAttachments = value.filter(
      a => a.mimeType?.startsWith('image/') && !previewUrls[a.id],
    );
    if (!imageAttachments.length) return;

    let cancelled = false;
    const newUrls: Record<string, string> = {};

    (async () => {
      for (const att of imageAttachments) {
        try {
          const res = await api.get(`/attachments/${att.id}/download`, { responseType: 'blob' });
          if (cancelled) break;
          newUrls[att.id] = URL.createObjectURL(res.data);
        } catch {
          // Preview unavailable; the file icon will show instead
        }
      }
      if (!cancelled && Object.keys(newUrls).length) {
        setPreviewUrls(prev => ({ ...prev, ...newUrls }));
      }
    })();

    return () => { cancelled = true; };
  }, [value]);

  // Revoke blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(previewUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  const handleDownload = async (attachment: UploadedAttachment) => {
    if (!skipWhenOffline()) {
      enqueueSnackbar('Downloading files requires a connection', { variant: 'info' });
      return;
    }
    try {
      const res = await api.get(`/attachments/${attachment.id}/download`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = attachment.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      enqueueSnackbar('Failed to download file', { variant: 'error' });
    }
  };

  const handleView = async (attachment: UploadedAttachment) => {
    const existing = previewUrls[attachment.id];
    if (existing) {
      window.open(existing, '_blank');
      return;
    }
    // Also reached via thumbnail/icon clicks, not just the (disabled) button.
    if (!skipWhenOffline()) {
      enqueueSnackbar('Viewing files requires a connection', { variant: 'info' });
      return;
    }
    try {
      const res = await api.get(`/attachments/${attachment.id}/download`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      setPreviewUrls(prev => ({ ...prev, [attachment.id]: blobUrl }));
      window.open(blobUrl, '_blank');
    } catch {
      enqueueSnackbar('Failed to open file', { variant: 'error' });
    }
  };

  const uploadFile = async (file: File, parentIdOverride?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (horseId) formData.append('horseId', horseId);
    if (parentType) formData.append('parentType', parentType);
    const pid = parentIdOverride ?? parentId;
    if (pid) formData.append('parentId', pid);
    if (fieldKey) formData.append('fieldKey', fieldKey);
    if (category) formData.append('category', category);

    const response = await api.post('/attachments/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e: any) => {
        if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
    return response.data as UploadedAttachment;
  };

  const handleFiles = useCallback(async (files: FileList) => {
    if (!files.length) return;
    // Mirror the LabsDiagnosticsPanel Import gate: file bytes are never queued
    // offline, so block up front instead of failing per-file.
    if (!skipWhenOffline()) {
      enqueueSnackbar('Uploading files requires a connection', { variant: 'info' });
      return;
    }

    // Resolve a parent id just-in-time (e.g. auto-save a brand-new SOAP note as
    // a draft) so the user can upload without a manual "save first" step.
    let resolvedParentId = parentId;
    if (!resolvedParentId && ensureParentId) {
      resolvedParentId = (await ensureParentId()) || undefined;
      if (!resolvedParentId) return; // ensureParentId surfaced its own error
    }

    setUploading(true);
    setUploadProgress(0);

    const toUpload = multiple ? Array.from(files) : [files[0]];
    const uploaded: UploadedAttachment[] = [];

    for (const file of toUpload) {
      try {
        const attachment = await uploadFile(file, resolvedParentId);
        uploaded.push(attachment);
      } catch {
        enqueueSnackbar(`Failed to upload ${file.name}`, { variant: 'error' });
      }
    }

    setUploading(false);
    setUploadProgress(0);
    if (uploaded.length) {
      onChange?.([...value, ...uploaded]);
      enqueueSnackbar(`${uploaded.length} file(s) uploaded`, { variant: 'success' });
    }
  }, [value, onChange, multiple, horseId, parentType, parentId, fieldKey, category, ensureParentId]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (!readOnly) handleFiles(e.dataTransfer.files);
  };

  const handleAIProcess = async (attachment: UploadedAttachment) => {
    setProcessingId(attachment.id);
    try {
      const response = await api.post(`/attachments/${attachment.id}/process`, {
        targetSchema: templateSchema,
      });
      const { aiExtracted } = response.data;
      onAIExtract?.(attachment.id, aiExtracted);
      // Update local attachment with extracted data
      onChange?.(value.map(a => a.id === attachment.id ? { ...a, aiExtracted } : a));
      enqueueSnackbar('AI extraction complete', { variant: 'success' });
    } catch {
      enqueueSnackbar('AI extraction failed', { variant: 'error' });
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    // Attachments aren't an offline-writable entity — a fire-and-forget delete
    // here would be silently LOST (never queued) while the UI reports success.
    if (!skipWhenOffline()) {
      enqueueSnackbar('Removing attachments requires a connection', { variant: 'info' });
      return;
    }
    try {
      await api.delete(`/attachments/${id}`);
    } catch {
      enqueueSnackbar('Failed to remove attachment', { variant: 'error' });
      return;
    }
    onChange?.(value.filter(a => a.id !== id));
  };

  const canProcess = (a: UploadedAttachment) => {
    const processable = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    return a.mimeType && processable.includes(a.mimeType);
  };

  return (
    <Box>
      {/* Drop zone — disabled offline (handleFiles also early-returns, so a
          drop while offline is blocked either way) */}
      {!readOnly && (
        <Box
          onDragOver={e => { e.preventDefault(); if (!isOffline) setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => {
            if (isOffline) {
              enqueueSnackbar('Uploading files requires a connection', { variant: 'info' });
              return;
            }
            inputRef.current?.click();
          }}
          sx={{
            border: '2px dashed',
            borderColor: dragging ? 'primary.main' : 'divider',
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
            cursor: isOffline ? 'not-allowed' : 'pointer',
            opacity: isOffline ? 0.6 : 1,
            bgcolor: dragging ? 'action.hover' : 'background.paper',
            transition: 'all 0.15s',
            '&:hover': isOffline ? {} : { borderColor: 'primary.light', bgcolor: 'action.hover' },
          }}
        >
          <UploadIcon color={dragging && !isOffline ? 'primary' : 'disabled'} sx={{ fontSize: 32, mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary">
            {isOffline
              ? 'Uploading files requires a connection'
              : dragging ? 'Drop to upload' : `Click or drag ${multiple ? 'files' : 'a file'} here`}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {accept !== '*' ? accept.replace(/\*/g, 'all') : 'Any file type'} · Max 20MB
          </Typography>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            style={{ display: 'none' }}
            onChange={e => e.target.files && handleFiles(e.target.files)}
          />
        </Box>
      )}

      {/* Upload progress */}
      {uploading && (
        <Box mt={1}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption" color="text.secondary">{uploadProgress}%</Typography>
        </Box>
      )}

      {/* File list */}
      {value.length > 0 && (
        <Box mt={readOnly ? 0 : 1.5} display="flex" flexDirection="column" gap={1}>
          {value.map(attachment => {
            const isImage = attachment.mimeType?.startsWith('image/');
            const thumbUrl = previewUrls[attachment.id];
            return (
              <Box
                key={attachment.id}
                display="flex"
                alignItems="center"
                gap={1.5}
                sx={{
                  border: '1px solid',
                  borderColor: attachment.aiExtracted ? 'success.light' : 'grey.200',
                  borderRadius: 1.5,
                  p: 1,
                  bgcolor: 'background.paper',
                  transition: 'all 0.15s',
                  '&:hover': {
                    boxShadow: 1,
                    borderColor: 'grey.300',
                    '& .file-actions': { opacity: 1 },
                  },
                }}
              >
                {/* Thumbnail / icon */}
                {isImage && thumbUrl ? (
                  <Box
                    component="img"
                    src={thumbUrl}
                    alt={attachment.fileName}
                    onClick={() => handleView(attachment)}
                    sx={{
                      width: 110,
                      height: 72,
                      objectFit: 'cover',
                      borderRadius: 1,
                      cursor: 'pointer',
                      flexShrink: 0,
                      bgcolor: 'grey.100',
                      '&:hover': { opacity: 0.85 },
                    }}
                  />
                ) : (
                  <Box
                    onClick={() => handleView(attachment)}
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1.5,
                      bgcolor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'grey.200',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: 'text.secondary',
                      cursor: 'pointer',
                      '& svg': { fontSize: 22 },
                    }}
                  >
                    {getFileIcon(attachment.mimeType)}
                  </Box>
                )}

                {/* File info */}
                <Box flex={1} minWidth={0}>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    noWrap
                    title={attachment.fileName}
                    sx={{ color: 'text.primary' }}
                  >
                    {attachment.fileName}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.75} mt={0.25}>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                      {formatBytes(attachment.fileSize)}
                    </Typography>
                    {attachment.aiExtracted && (
                      <Chip
                        label="AI processed"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '10px', fontWeight: 600 }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Actions — slightly faded, full opacity on row hover. They
                    need the server (blob download / AI extract) so they're
                    disabled offline — the file list itself stays visible
                    (names/sizes come from the saved note). span wrappers keep
                    the tooltips working while the buttons are disabled. */}
                <Box className="file-actions" display="flex" gap={0.25} flexShrink={0} sx={{ opacity: 0.6, transition: 'opacity 0.15s' }}>
                  {canProcess(attachment) && onAIExtract && !readOnly && (
                    <Tooltip title={isOffline ? 'Available when back online' : 'Extract data with AI'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleAIProcess(attachment)}
                          disabled={isOffline || processingId === attachment.id}
                          sx={{ color: 'primary.main' }}
                        >
                          {processingId === attachment.id
                            ? <CircularProgress size={16} />
                            : <AIIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title={isOffline ? 'Available when back online' : 'View'}>
                    <span>
                      <IconButton size="small" onClick={() => handleView(attachment)} disabled={isOffline} sx={{ color: 'text.secondary' }}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={isOffline ? 'Available when back online' : 'Download'}>
                    <span>
                      <IconButton size="small" onClick={() => handleDownload(attachment)} disabled={isOffline} sx={{ color: 'text.secondary' }}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {!readOnly && (
                    <Tooltip title="Remove">
                      <IconButton size="small" onClick={() => handleDelete(attachment.id)} sx={{ color: 'error.main' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default FileDropZone;
